import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Card,
  Col,
  Input,
  Row,
  Select,
  Segmented,
  Spin,
  Tag,
  Typography,
} from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import uPlot, { type AlignedData, type Options } from "uplot";
import "uplot/dist/uPlot.min.css";
import {
  api,
  type IPNode,
  type IPProtoSeriesResp,
  type ProtoPoint,
} from "../api";
import { useGeoSettings } from "../contexts/GeoSettingsContext";
import { useIPZone } from "../contexts/IPZoneContext";

const { Text } = Typography;

// ── format helpers ────────────────────────────────────────────────────────────

// Full format for legend / tooltip (hover values)
function fmtBps(v: number | null): string {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(2) + " Gbps";
  if (a >= 1e6) return (v / 1e6).toFixed(2) + " Mbps";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kbps";
  return Math.round(v) + " bps";
}

// Short format for Y-axis tick labels — prevents clipping in narrow axis.
function fmtBpsTick(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return (a / 1e9).toFixed(1) + " G";
  if (a >= 1e6) return (a / 1e6).toFixed(0) + " M";
  if (a >= 1e3) return (a / 1e3).toFixed(0) + " k";
  return Math.round(a) + "";
}

const RANGES = [
  { label: "Last 5 min",    value: 5 * 60 },
  { label: "Last 10 min",   value: 10 * 60 },
  { label: "Last 15 min",   value: 15 * 60 },
  { label: "Last Hour",     value: 3600 },
  { label: "Last 3 Hours",  value: 3 * 3600 },
  { label: "Last 12 Hours", value: 12 * 3600 },
  { label: "Last Day",      value: 24 * 3600 },
  { label: "Last Week",     value: 7 * 24 * 3600 },
];

const REFRESHES = [
  { label: "off",        value: 0 },
  { label: "5 seconds",  value: 5_000 },
  { label: "30 seconds", value: 30_000 },
  { label: "1 minute",   value: 60_000 },
  { label: "5 minutes",  value: 5 * 60_000 },
];

type ViewMode = "total" | "nix" | "ixp";

const ALL_VIEW_OPTIONS = [
  { label: "Total",  value: "total" as ViewMode },
  { label: "NIX",   value: "nix"   as ViewMode },
  { label: "IXP",   value: "ixp"   as ViewMode },
];

// ── protocol color map ────────────────────────────────────────────────────────

// Only these 7 decoders are displayed — matches the Wansight reference legend.
const DISPLAY_DECODERS = ["IP", "TCP", "TCP+SYN", "UDP", "ICMP", "DNS", "NTP"] as const;

const PROTO_COLORS: Record<string, string> = {
  "IP":      "#06a131",
  "TCP":     "#6766ff",
  "TCP+SYN": "#ff4444",
  "UDP":     "#ff9524",
  "ICMP":    "#679900",
  "DNS":     "#ffc93f",
  "NTP":     "#ffc93f",
};

// ── legend format helpers ─────────────────────────────────────────────────────

function fmtBpsLegend(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(1) + " Gbits/s";
  if (a >= 1e6) return (v / 1e6).toFixed(1) + " Mbits/s";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kbits/s";
  return v.toFixed(1) + " bits/s";
}

function fmtBitsSum(bits: number): string {
  const a = Math.abs(bits);
  if (a >= 1e9) return (bits / 1e9).toFixed(1) + " Gbits";
  if (a >= 1e6) return (bits / 1e6).toFixed(1) + " Mbits";
  if (a >= 1e3) return (bits / 1e3).toFixed(1) + " kbits";
  return bits.toFixed(1) + " bits";
}

// PPS formatters
function fmtPps(v: number | null): string {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e6) return (v / 1e6).toFixed(2) + " Mpps";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kpps";
  return Math.round(v) + " pps";
}
function fmtPpsLegend(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e6) return (v / 1e6).toFixed(2) + " Mpps";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " kpps";
  return v.toFixed(1) + " pps";
}
function fmtPpsSum(pkts: number): string {
  const a = Math.abs(pkts);
  if (a >= 1e9) return (pkts / 1e9).toFixed(1) + " Gpkts";
  if (a >= 1e6) return (pkts / 1e6).toFixed(1) + " Mpkts";
  if (a >= 1e3) return (pkts / 1e3).toFixed(1) + " kpkts";
  return Math.round(pkts) + " pkts";
}
function fmtPpsTick(v: number): string {
  const a = Math.abs(v);
  if (a >= 1e6) return (a / 1e6).toFixed(1) + " M";
  if (a >= 1e3) return (a / 1e3).toFixed(0) + " k";
  return Math.round(a) + "";
}

type MetricMode = "bps" | "pps";

// ── ProtoLegend table ─────────────────────────────────────────────────────────

function ProtoLegend({
  decoders, points, metric,
}: { decoders: string[]; points: ProtoPoint[]; metric: MetricMode }) {
  const totalSecs = points.length > 1 ? points[points.length - 1].t - points[0].t : 0;
  const isPPS = metric === "pps";

  const stats = decoders.map((dec) => {
    const inKey  = isPPS ? "in_pps"  : "in";
    const outKey = isPPS ? "out_pps" : "out";
    const inVals  = points.map((p) => (p[inKey]  as Record<string, number>)[dec]  ?? 0);
    const outVals = points.map((p) => (p[outKey] as Record<string, number>)[dec] ?? 0);
    const inMax   = Math.max(...inVals);
    const inMin   = Math.min(...inVals);
    const inAvg   = inVals.reduce((a, b) => a + b, 0) / (inVals.length || 1);
    const outMax  = Math.max(...outVals);
    const outMin  = Math.min(...outVals);
    const outAvg  = outVals.reduce((a, b) => a + b, 0) / (outVals.length || 1);
    return {
      dec,
      inMax, inMin, inAvg, inSum: inAvg * totalSecs,
      outMax, outMin, outAvg, outSum: outAvg * totalSecs,
    };
  });

  const fmtVal = isPPS ? fmtPpsLegend : fmtBpsLegend;
  const fmtSum = isPPS ? fmtPpsSum    : fmtBitsSum;

  const cell: React.CSSProperties = {
    fontFamily: "monospace", fontSize: 11, minWidth: 150, display: "inline-block",
  };
  const nameCell: React.CSSProperties = {
    fontFamily: "monospace", fontSize: 11, width: 72, display: "inline-block",
  };
  const sumCell: React.CSSProperties = {
    fontFamily: "monospace", fontSize: 11, minWidth: 130, display: "inline-block",
  };
  const headerStyle: React.CSSProperties = {
    fontWeight: "bold", fontSize: 11, padding: "5px 0 2px", fontFamily: "sans-serif",
  };

  const renderSection = (label: string, dir: "in" | "out") => (
    <div>
      <div style={headerStyle}>{label}</div>
      {stats.map((s) => {
        const color  = PROTO_COLORS[s.dec] ?? "#999";
        const max    = dir === "in" ? s.inMax  : s.outMax;
        const min    = dir === "in" ? s.inMin  : s.outMin;
        const avg    = dir === "in" ? s.inAvg  : s.outAvg;
        const sum    = dir === "in" ? s.inSum  : s.outSum;
        return (
          <div key={s.dec} style={{ display: "flex", alignItems: "center", lineHeight: "18px" }}>
            <span style={{ display: "inline-block", width: 10, height: 10, background: color, marginRight: 4, flexShrink: 0 }} />
            <span style={nameCell}>{s.dec}</span>
            <span style={cell}>Max = {fmtVal(max)}</span>
            <span style={cell}>Avg = {fmtVal(avg)}</span>
            <span style={cell}>Min = {fmtVal(min)}</span>
            <span style={sumCell}>Sum = {fmtSum(sum)}</span>
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ marginTop: 10, overflowX: "auto" }}>
      {renderSection("INBOUND:", "in")}
      <div style={{ marginTop: 6 }}>
        {renderSection("OUTBOUND:", "out")}
      </div>
    </div>
  );
}

// ── ProtoChart (zone node / individual host / CIDR range) ────────────────────

interface ProtoChartProps {
  nodeID?: number;   // exact zone node → ipNodeProtoSeries / ipNodeGeoProtoSeries
  addr?: string;     // individual IP   → ipHostProtoSeries / ipHostGeoProtoSeries
  prefix?: string;   // CIDR range      → ipCIDRProtoSeries / ipCIDRGeoProtoSeries
  zoneID?: number;   // whole zone      → zoneProtoSeries (no geo variant yet)
  seconds: number;
  nonce: number;
  geoClass?: "nix" | "ixp"; // when set, fetches from geo-proto endpoints
}

function ProtoChart({
  nodeID,
  addr,
  prefix,
  zoneID,
  seconds,
  nonce,
  geoClass,
}: ProtoChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const [protoData, setProtoData] = useState<IPProtoSeriesResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [metric, setMetric] = useState<MetricMode>("bps");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    // zone-aggregate mode has no geo variant yet — ignore geoClass when zoneID is set.
    const fetchFn = zoneID != null
      ? api.zoneProtoSeries(zoneID, seconds)
      : geoClass != null
      ? (prefix != null
          ? api.ipCIDRGeoProtoSeries(prefix, seconds, geoClass)
          : addr != null
            ? api.ipHostGeoProtoSeries(addr, seconds, geoClass)
            : api.ipNodeGeoProtoSeries(nodeID!, seconds, geoClass))
      : (prefix != null
          ? api.ipCIDRProtoSeries(prefix, seconds)
          : addr != null
            ? api.ipHostProtoSeries(addr, seconds)
            : api.ipNodeProtoSeries(nodeID!, seconds));

    fetchFn
      .then((d) => {
        if (cancelled) return;
        setProtoData(d);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [nodeID, addr, prefix, zoneID, seconds, nonce, geoClass]);

  // Rebuild chart whenever data or metric mode changes.
  useEffect(() => {
    if (!protoData || !ref.current) return;

    const isPPS = metric === "pps";
    const inKey  = isPPS ? "in_pps"  : "in"  as const;
    const outKey = isPPS ? "out_pps" : "out" as const;
    const fmtLive = isPPS ? fmtPps : fmtBps;
    const fmtTick = isPPS ? fmtPpsTick : fmtBpsTick;

    // Filter to only the 7 display decoders, in canonical order.
    const decoders = protoData.decoders.filter((d) => (DISPLAY_DECODERS as readonly string[]).includes(d));
    const { points } = protoData;
    const ts = points.map((p) => p.t);

    // Build aligned data: [ts, dec0_in, dec0_out_neg, dec1_in, dec1_out_neg, ...]
    const buildAligned = (): AlignedData => {
      const aligned: AlignedData = [ts];
      for (const dec of decoders) {
        aligned.push(points.map((p) => (p[inKey]  as Record<string, number>)[dec]  ?? null));
        aligned.push(points.map((p) => {
          const v = (p[outKey] as Record<string, number>)[dec];
          return v ? -v : null;
        }));
      }
      return aligned;
    };

    const alignedData = buildAligned();

    // If plot exists and only data/metric changed but decoders are same — just setData.
    if (plotRef.current && plotRef.current.series.length === decoders.length * 2 + 1) {
      plotRef.current.setData(alignedData);
      // Update Y-axis formatter by rebuilding (cheapest approach: destroy + recreate).
      plotRef.current.destroy();
      plotRef.current = null;
    }

    if (plotRef.current) {
      plotRef.current.setData(alignedData);
      return;
    }

    const seriesDefs = decoders.flatMap((dec) => {
      const color = PROTO_COLORS[dec] ?? "#999";
      return [
        {
          label: `${dec} in`,
          stroke: color,
          fill: color + "bb",
          width: 1,
          value: (_u: uPlot, v: number | null) => fmtLive(v),
        },
        {
          label: `${dec} out`,
          stroke: color,
          fill: color + "bb",
          width: 1,
          value: (_u: uPlot, v: number | null) => fmtLive(v == null ? null : -v),
        },
      ];
    });

    const opts: Options = {
      width: ref.current.clientWidth,
      height: 280,
      cursor: { drag: { x: false, y: false } },
      legend: { show: false },
      scales: { x: { time: true }, y: { auto: true } },
      axes: [
        { stroke: "#888", grid: { stroke: "#eee", width: 1 }, font: "11px sans-serif" },
        {
          stroke: "#888",
          grid: { stroke: "#eee", width: 1 },
          font: "11px sans-serif",
          size: 52,
          gap: 6,
          values: (_u, ticks) => ticks.map(fmtTick),
        },
      ],
      series: [{}, ...seriesDefs],
    };

    plotRef.current = new uPlot(opts, alignedData, ref.current);

    const ro = new ResizeObserver(() => {
      if (plotRef.current && ref.current)
        plotRef.current.setSize({ width: ref.current.clientWidth, height: 280 });
    });
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, [protoData, metric]);

  if (loading) return <Spin style={{ display: "block", padding: 40 }} />;
  if (error)   return <Alert type="error" message={error} />;

  const displayDecoders = (protoData?.decoders ?? []).filter((d) => (DISPLAY_DECODERS as readonly string[]).includes(d));
  const hasData = protoData && protoData.points.length > 0 && displayDecoders.length > 0;

  return (
    <div style={{ position: "relative" }}>
      {/* BPS / PPS toggle */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
        <Segmented
          size="small"
          value={metric}
          onChange={(v) => setMetric(v as MetricMode)}
          options={[
            { label: "bps", value: "bps" },
            { label: "pps", value: "pps" },
          ]}
        />
      </div>
      <div ref={ref} style={{ width: "100%" }} />
      {!hasData && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#999", fontSize: 13 }}>
          No data in this time range
        </div>
      )}
      {hasData && (
        <ProtoLegend decoders={displayDecoders} points={protoData!.points} metric={metric} />
      )}
    </div>
  );
}

// ── NodeChart dispatcher ──────────────────────────────────────────────────────

function NodeChart({
  nodeID,
  addr,
  match,
  seconds,
  nonce,
  view,
}: {
  nodeID?: number;
  addr?: string;
  match?: string;
  seconds: number;
  nonce: number;
  view: ViewMode;
}) {
  const geoClass = (view === "nix" || view === "ixp") ? view : undefined;

  if (match === "zone") {
    // nodeID holds the zone id in this mode. No geo variant yet.
    return <ProtoChart zoneID={nodeID} seconds={seconds} nonce={nonce} />;
  }
  if (match === "host") {
    return <ProtoChart addr={addr} seconds={seconds} nonce={nonce} geoClass={geoClass} />;
  }
  if (match === "range") {
    return <ProtoChart prefix={addr} seconds={seconds} nonce={nonce} geoClass={geoClass} />;
  }
  return <ProtoChart nodeID={nodeID} seconds={seconds} nonce={nonce} geoClass={geoClass} />;
}

// ── result card ───────────────────────────────────────────────────────────────

function NodeCard({ node, seconds, nonce, view }: { node: IPNode; seconds: number; nonce: number; view: ViewMode }) {
  const matchColor: Record<string, string> = {
    host: "purple", range: "blue", exact: "green", parent: "geekblue", child: "orange",
    zone: "magenta",
  };
  const matchLabel: Record<string, string> = {
    host: "individual IP", range: "CIDR aggregate", exact: "exact match",
    parent: "parent subnet", child: "child subnet",
    zone: "whole IP zone",
  };

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <span style={{ fontFamily: "monospace", fontSize: 14 }}>
          {node.prefix}
          {node.description && node.match !== "host" && node.match !== "range" && (
            <Text type="secondary" style={{ marginLeft: 10, fontSize: 12, fontFamily: "sans-serif" }}>
              {node.description}
            </Text>
          )}
        </span>
      }
      extra={<Tag color={matchColor[node.match] ?? "default"}>{matchLabel[node.match] ?? node.match}</Tag>}
    >
      <NodeChart nodeID={node.id || undefined} addr={node.addr} match={node.match} seconds={seconds} nonce={nonce} view={view} />
    </Card>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function IPAddresses() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [input, setInput] = useState("");
  const [nodes, setNodes] = useState<IPNode[]>([]);
  const [seconds, setSeconds] = useState(600);
  const [refreshMs, setRefreshMs] = useState(0);
  const [nonce, setNonce] = useState(0);
  const [view, setView] = useState<ViewMode>("total");
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [searchError, setSearchError] = useState("");
  const { settings: geoSettings } = useGeoSettings();
  const { zones } = useIPZone();

  // Build available view options based on which geo classes are enabled.
  const viewOptions = useMemo(() => {
    return ALL_VIEW_OPTIONS.filter((o) => {
      if (o.value === "nix") return geoSettings.nix_enabled;
      if (o.value === "ixp") return geoSettings.ixp_enabled;
      return true; // "total" is always available
    });
  }, [geoSettings.nix_enabled, geoSettings.ixp_enabled]);

  // If the currently selected view becomes unavailable, fall back to "total".
  useEffect(() => {
    const available = viewOptions.map((o) => o.value);
    if (!available.includes(view)) {
      setView("total");
    }
  }, [viewOptions, view]);

  const doSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setSearching(true);
    setNotFound(false);
    setSearchError("");
    setNodes([]);
    try {
      const resp = await api.ipSearch(trimmed);
      setQuery(resp.query);
      setNodes(resp.nodes);
      setNotFound(resp.nodes.length === 0);
    } catch (e) {
      setSearchError(e instanceof Error ? e.message : "search failed");
    } finally {
      setSearching(false);
    }
  }, []);

  // Auto-search when navigated from sidebar with ?q= param; ?view= sets initial view mode.
  // ?zone=<id> synthesises a single "whole zone" node card and skips ipSearch entirely.
  useEffect(() => {
    const q = searchParams.get("q");
    const v = searchParams.get("view");
    const zoneParam = searchParams.get("zone");
    if (v === "nix" || v === "ixp" || v === "total") setView(v as ViewMode);

    if (zoneParam) {
      const zoneID = parseInt(zoneParam, 10);
      if (Number.isFinite(zoneID)) {
        const z = zones.find((x) => x.id === zoneID);
        const label = `zone: ${z ? z.name : `#${zoneID}`}`;
        setInput(label);
        setQuery(label);
        setNotFound(false);
        setSearchError("");
        setSearching(false);
        setNodes([
          {
            id: zoneID,
            prefix: label,
            description: z ? `${z.name} — all prefixes` : "",
            match: "zone" as IPNode["match"],
          },
        ]);
      }
      return;
    }

    if (q) { setInput(q); doSearch(q); }
  }, [searchParams, doSearch, zones]);

  // Auto-refresh: bump nonce every refreshMs
  useEffect(() => {
    if (refreshMs <= 0 || nodes.length === 0) return;
    const id = window.setInterval(() => setNonce((n) => n + 1), refreshMs);
    return () => window.clearInterval(id);
  }, [refreshMs, nodes.length]);

  return (
    <div>
      {/* Search + controls bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={8} align="middle">
          <Col flex="1">
            <Input
              size="large"
              prefix={<SearchOutlined style={{ color: "#aaa" }} />}
              placeholder="Enter IP or CIDR — e.g.  116.118.47.215  or  116.118.47.0/24"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onPressEnter={() => doSearch(input)}
              allowClear
              style={{ fontFamily: "monospace" }}
            />
          </Col>
          {/* View */}
          <Col>
            <span style={{ fontSize: 11, color: "#667", marginRight: 4 }}>View</span>
            <Select
              size="large"
              value={view}
              onChange={(v) => { setView(v); setNonce((n) => n + 1); }}
              options={viewOptions}
              style={{ width: 100 }}
            />
          </Col>
          {/* Time Range */}
          <Col>
            <span style={{ fontSize: 11, color: "#667", marginRight: 4 }}>Time Range</span>
            <Select
              size="large"
              value={seconds}
              onChange={(v) => { setSeconds(v); setNonce((n) => n + 1); }}
              options={RANGES}
              style={{ width: 140 }}
            />
          </Col>
          {/* Refresh */}
          <Col>
            <span style={{ fontSize: 11, color: "#667", marginRight: 4 }}>Refresh</span>
            <Select
              size="large"
              value={refreshMs}
              onChange={setRefreshMs}
              options={REFRESHES}
              style={{ width: 120 }}
              suffixIcon={
                <ReloadOutlined
                  style={{ cursor: "pointer", color: "#c0392b" }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setNonce((n) => n + 1);
                  }}
                />
              }
            />
          </Col>
        </Row>
        <div style={{ marginTop: 6, fontSize: 11, color: "#aaa" }}>
          Press Enter to search. Accepts IPv4/IPv6 with or without prefix length.
        </div>
      </Card>

      {/* Results */}
      {searching && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin tip="Searching…" />
        </div>
      )}

      {searchError && <Alert type="error" message={searchError} style={{ marginBottom: 12 }} />}

      {notFound && (
        <Alert
          type="info"
          message={`No monitored subnet found for "${query}". Check that the IP is in a configured IP Zone.`}
          style={{ marginBottom: 12 }}
        />
      )}

      {nodes.length > 0 && (
        <>
          <div style={{ marginBottom: 8, fontSize: 12, color: "#555" }}>
            {nodes.length} matching subnet{nodes.length > 1 ? "s" : ""} for{" "}
            <Text code>{query}</Text>
          </div>
          {nodes.map((n) => (
            <NodeCard key={n.id} node={n} seconds={seconds} nonce={nonce} view={view} />
          ))}
        </>
      )}

      {!searching && nodes.length === 0 && !notFound && !searchError && (
        <div style={{ textAlign: "center", color: "#bbb", padding: "60px 0", fontSize: 14 }}>
          <SearchOutlined style={{ fontSize: 48, marginBottom: 12, display: "block" }} />
          Type an IP address or subnet above and press Enter
        </div>
      )}
    </div>
  );
}
